<template>
  <div class="reset-password-container">
    <div class="page-title">重置密码</div>
    <p class="page-subtitle">请填写相关信息以重置您的账户密码</p>

    <!-- 步骤指示器 -->
    <div class="step-indicator">
      <div class="step-item" :class="{ active: currentStep === 1, completed: currentStep > 1 }">
        <div class="step-number">{{ currentStep > 1 ? '✓' : '1' }}</div>
        <div class="step-label">验证身份</div>
      </div>
      <div class="step-item" :class="{ active: currentStep === 2, completed: currentStep > 2 }">
        <div class="step-number">{{ currentStep > 2 ? '✓' : '2' }}</div>
        <div class="step-label">设置新密码</div>
      </div>
      <div class="step-item" :class="{ active: currentStep === 3, completed: currentStep > 3 }">
        <div class="step-number">3</div>
        <div class="step-label">完成</div>
      </div>
    </div>

    <!-- 表单区域 -->
    <el-form ref="resetForm" :model="form" :rules="rules" label-position="top" size="medium">
      <!-- 第一步：手机号码和验证码 -->
      <div class="form-section" v-show="currentStep === 1">
        <div class="section-title">身份验证</div>

        <el-form-item label="产品" prop="dbSource">
          <el-select v-model="form.dbSource" placeholder="请选择产品" class="db-source-select">
            <el-option v-for="item in dbSourceOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>

        <el-form-item label="手机号码" prop="telAccountId">
          <el-input v-model="form.telAccountId" placeholder="请输入要修改密码的手机号码" prefix-icon="el-icon-mobile-phone"
            maxlength="11" clearable />
        </el-form-item>

        <el-form-item label="验证码" prop="verifyCode">
          <div class="input-with-btn">
            <el-input v-model="form.verifyCode" placeholder="请输入 6 位验证码" maxlength="6" prefix-icon="el-icon-lock"
              clearable />
            <el-button class="verify-code-btn" type="primary" plain :disabled="countdown > 0 || !canSendCode"
              @click="sendVerifyCode">
              {{ countdown > 0 ? `${countdown}秒后重发` : '获取验证码' }}
            </el-button>
          </div>
        </el-form-item>

        <div class="tips" v-if="currentStep === 1">
          <div class="tips-content">
            <i class="el-icon-info tips-icon"></i>
            <div class="tips-text">
              <strong>温馨提示：</strong><br>
              1. 验证码将发送到当前登录用户的手机号码<br>
              2. 验证码有效期为 60 秒，请及时使用<br>
              3. 每个手机号每天最多可获取 10 次验证码
            </div>
          </div>
        </div>

        <el-button class="submit-btn" type="primary" size="large" round :loading="loading" @click="validateCode">
          验证并继续
        </el-button>
      </div>

      <!-- 第二步：设置新密码 -->
      <div class="form-section" v-show="currentStep === 2">
        <div class="section-title">设置新密码</div>

        <el-form-item label="新密码" prop="newPassword">
          <el-input v-model="form.newPassword" type="password" placeholder="请输入新密码（6-20 位，包含字母和数字）" show-password
            clearable @input="checkPasswordStrength" />
          <div class="password-strength" v-if="form.newPassword">
            <div class="strength-header">
              <span class="strength-label">密码强度</span>
              <span class="strength-text" :class="strengthClass">{{ strengthText }}</span>
            </div>
            <div class="strength-bar">
              <div class="strength-fill" :class="strengthClass"></div>
            </div>
          </div>
        </el-form-item>

        <el-form-item label="确认密码" prop="confirmPassword">
          <el-input v-model="form.confirmPassword" type="password" placeholder="请再次输入新密码" show-password clearable />
        </el-form-item>

        <div class="form-actions">
          <el-button class="action-btn" size="large" round @click="currentStep = 1">
            上一步
          </el-button>
          <el-button class="action-btn submit-btn" type="primary" size="large" round :loading="loading"
            @click="submitChangePassword">
            确认重置密码
          </el-button>
        </div>
      </div>

      <!-- 第三步：完成 -->
      <div class="form-section success-section" v-show="currentStep === 3">
        <div class="success-icon">
          <i class="el-icon-circle-check"></i>
        </div>
        <div class="success-title">密码重置成功</div>
        <div class="success-message">
          您的密码已成功重置，请使用新密码登录
        </div>
        <el-button class="submit-btn" type="primary" size="large" round @click="goBack">
          返回上一页
        </el-button>
      </div>
    </el-form>
  </div>
</template>

<script>
import api from '@/utils/api'

export default {
  name: 'ResetPassword',
  data () {
    // 手机号验证规则
    const validateMobile = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请输入手机号码'))
      } else if (!/^1[3-9]\d{9}$/.test(value)) {
        callback(new Error('请输入正确的手机号码'))
      } else {
        callback()
      }
    }

    // 验证码验证规则
    const validateVerifyCode = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请输入验证码'))
      } else if (!/^\d{6}$/.test(value)) {
        callback(new Error('验证码必须是 6 位数字'))
      } else {
        callback()
      }
    }

    // 密码强度验证规则
    const validatePassword = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请输入新密码'))
      } else if (value.length < 6 || value.length > 20) {
        callback(new Error('密码长度必须在 6-20 位之间'))
      } else if (!/(?=.*[0-9])(?=.*[a-zA-Z])/.test(value)) {
        callback(new Error('密码必须包含字母和数字'))
      } else {
        callback()
      }
    }

    // 确认密码验证规则
    const validateConfirmPassword = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请再次输入新密码'))
      } else if (value !== this.form.newPassword) {
        callback(new Error('两次输入的密码不一致'))
      } else {
        callback()
      }
    }

    return {
      currentStep: 1,
      loading: false,
      countdown: 0,
      countdownTimer: null,
      canSendCode: true,
      passwordStrength: 0,
      dbSourceOptions: [
        { label: '加油站', value: 0 },
        { label: '剑桥少儿英语168版', value: 1 },
        { label: '新知堂英语1To1', value: 2 }
      ],
      form: {
        dbSource: 0,
        telAccountId: '',
        verifyCode: '',
        newPassword: '',
        confirmPassword: ''
      },
      rules: {
        dbSource: [
          { required: true, message: '请选择产品', trigger: 'change' }
        ],
        telAccountId: [
          { required: true, validator: validateMobile, trigger: 'blur' }
        ],
        verifyCode: [
          { required: true, validator: validateVerifyCode, trigger: 'blur' }
        ],
        newPassword: [
          { required: true, validator: validatePassword, trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, validator: validateConfirmPassword, trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    strengthClass () {
      if (this.passwordStrength <= 1) return 'weak'
      if (this.passwordStrength <= 2) return 'medium'
      return 'strong'
    },
    strengthText () {
      if (this.passwordStrength <= 1) return '弱'
      if (this.passwordStrength <= 2) return '中'
      return '强'
    }
  },
  beforeDestroy () {
    // 清除定时器
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer)
    }
  },
  methods: {
    /**
     * 发送验证码
     */
    async sendVerifyCode () {
      // 先验证手机号
      try {
        await this.$refs.resetForm.validateField('telAccountId')
      } catch (error) {
        return
      }

      this.loading = true
      try {
        const response = await this.$axios.post(api.sendVerifyCode, {
          TelAccountId: this.form.telAccountId,
          dbSource: this.form.dbSource
        })

        if (response.data.success) {
          this.$message({
            message: '验证码已发送，请注意查收短信',
            type: 'success'
          })
          // 开始倒计时
          this.startCountdown()
        } else {
          this.$message({
            message: response.data.result.message || '验证码发送失败',
            type: 'error'
          })
        }
      } catch (error) {
        console.error('发送验证码失败:', error)
        this.$message({
          message: error.response.data.result.message || '验证码发送失败，请稍后重试',
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },

    /**
     * 开始倒计时
     */
    startCountdown () {
      this.countdown = 60
      this.canSendCode = false
      this.countdownTimer = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown--
        } else {
          clearInterval(this.countdownTimer)
          this.canSendCode = true
        }
      }, 1000)
    },

    /**
     * 验证验证码
     */
    async validateCode () {
      try {
        // 1. 前端表单格式验证
        const valid = await this.$refs.resetForm.validateField(['telAccountId', 'verifyCode'])
        if (valid !== false) {
          // 2. 调用后端验证验证码正确性
          this.loading = true
          const response = await this.$axios.post(api.validateVerifyCode, {
            TelAccountId: this.form.telAccountId,
            VerifyCode: this.form.verifyCode,
            dbSource: this.form.dbSource
          })

          // 3. 根据后端返回结果判断是否通过
          if (response.data.result.success) {
            // 验证通过，进入设置密码步骤
            this.currentStep = 2
            this.$message({
              message: '验证码验证通过，请设置新密码',
              type: 'success'
            })
          } else {
            // 验证失败，提示错误（保留用户输入的验证码）
            this.$message({
              message: response.data.result.message || '验证码错误，请重新输入',
              type: 'error'
            })
          }
        }
      } catch (error) {
        console.error('验证码验证失败:', error)
        this.$message({
          message: error.response.data.result.message || '验证码验证失败，请稍后重试',
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },

    /**
     * 检查密码强度
     */
    checkPasswordStrength () {
      const password = this.form.newPassword
      let strength = 0

      if (!password) {
        this.passwordStrength = 0
        return
      }

      // 长度检查
      if (password.length >= 8) strength++
      if (password.length >= 12) strength++

      // 字符类型检查
      if (/[0-9]/.test(password)) strength++
      if (/[a-zA-Z]/.test(password)) strength++
      if (/[^a-zA-Z0-9]/.test(password)) strength++

      // 归一化到 0-3
      this.passwordStrength = Math.min(Math.floor(strength / 2), 3)
    },

    /**
     * 提交密码修改
     */
    async submitChangePassword () {
      try {
        await this.$refs.resetForm.validate()

        this.loading = true
        const response = await this.$axios.post(api.changePassword, {
          TelAccountId: this.form.telAccountId,
          NewPassword: this.form.newPassword,
          ConfirmPassword: this.form.confirmPassword,
          VerifyCode: this.form.verifyCode,
          dbSource: this.form.dbSource
        })

        if (response.data.result.success) {
          this.$message({
            message: '密码修改成功',
            type: 'success'
          })
          this.currentStep = 3
        } else {
          this.$message({
            message: response.data.message || '密码修改失败',
            type: 'error'
          })
        }
      } catch (error) {
        if (error instanceof Error) {
          console.error('表单验证失败:', error)
        } else {
          console.error('修改密码失败:', error)
          this.$message({
            message: error.response.data.result.message || '密码修改失败，请稍后重试',
            type: 'error'
          })
        }
      } finally {
        this.loading = false
      }
    },

    /**
     * 返回上一页
     */
    goBack () {
      this.$router.go(-1)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';
@import '@/styles/mixins.scss';

.reset-password-container {
  padding: $spacing-xxl;
  background: var(--bg-white);
  border-radius: $border-radius-lg;
  box-shadow: var(--shadow-base);
  width: 100%;
  max-width: 520px;
  margin: 0 auto;
}

.page-title {
  text-align: center;
  color: var(--text-primary);
  font-size: $font-size-xxl;
  font-weight: 600;
  margin-bottom: $spacing-sm;
}

.page-subtitle {
  text-align: center;
  color: var(--text-secondary);
  font-size: $font-size-base;
  margin-bottom: $spacing-xl;
}

/* 步骤指示器 */
.step-indicator {
  display: flex;
  justify-content: space-between;
  margin-bottom: $spacing-xl;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 15px;
    left: 0;
    right: 0;
    height: 2px;
    background: var(--border-base);
    z-index: 0;
  }
}

.step-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
  flex: 1;
  transition: $transition-base;
}

.step-number {
  width: 32px;
  height: 32px;
  border-radius: $border-radius-circle;
  background: var(--border-base);
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: $font-size-sm;
  font-weight: 600;
  margin-bottom: $spacing-xs;
  transition: $transition-base;
}

.step-item.active .step-number {
  background: $primary-color;
  color: #fff;
}

.step-item.completed .step-number {
  background: $success-color;
  color: #fff;
}

.step-label {
  font-size: $font-size-xs;
  color: var(--text-secondary);
  transition: $transition-base;
}

.step-item.active .step-label {
  color: $primary-color;
  font-weight: 600;
}

.step-item.completed .step-label {
  color: $success-color;
}

/* 表单区域 */
.form-section {
  margin-bottom: $spacing-lg;
}

.section-title {
  font-size: $font-size-base;
  color: var(--text-regular);
  margin-bottom: $spacing-md;
  font-weight: 600;
}

.db-source-select {
  width: 100%;
}

.input-with-btn {
  display: flex;
  gap: $spacing-sm;

  .el-input {
    flex: 1;
  }
}

.verify-code-btn {
  white-space: nowrap;
  min-width: 120px;
}

.tips {
  background: $primary-lighter;
  border: 1px solid rgba($primary-color, 0.3);
  border-radius: $border-radius-sm;
  padding: $spacing-md;
  margin-top: $spacing-lg;
  margin-bottom: $spacing-lg;

  [data-theme='dark'] & {
    background: rgba($primary-color, 0.1);
    border-color: rgba($primary-color, 0.25);
  }
}

.tips-content {
  display: flex;
  align-items: flex-start;
  gap: $spacing-sm;
}

.tips-icon {
  color: $primary-color;
  font-size: $font-size-md;
  flex-shrink: 0;
}

.tips-text {
  font-size: $font-size-sm;
  color: var(--text-regular);
  line-height: 1.6;
}

.submit-btn {
  width: 100%;
  height: 44px;
  font-size: $font-size-md;
}

/* 密码强度 */
.password-strength {
  margin-top: $spacing-sm;
}

.strength-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.strength-label {
  font-size: $font-size-xs;
  color: var(--text-secondary);
}

.strength-text {
  font-size: $font-size-xs;
  font-weight: 500;

  &.weak {
    color: $danger-color;
  }

  &.medium {
    color: $warning-color;
  }

  &.strong {
    color: $success-color;
  }
}

.strength-bar {
  height: 4px;
  background: var(--border-base);
  border-radius: 2px;
  overflow: hidden;
}

.strength-fill {
  height: 100%;
  width: 0;
  transition: $transition-base;
  border-radius: 2px;

  &.weak {
    width: 33.33%;
    background: $danger-color;
  }

  &.medium {
    width: 66.66%;
    background: $warning-color;
  }

  &.strong {
    width: 100%;
    background: $success-color;
  }
}

/* 表单操作按钮 */
.form-actions {
  display: flex;
  gap: $spacing-md;
  margin-top: $spacing-lg;
}

.action-btn {
  flex: 1;
  height: 44px;
  font-size: $font-size-md;
}

/* 成功状态 */
.success-section {
  text-align: center;
  padding: $spacing-xxl $spacing-lg;
}

.success-icon {
  font-size: 80px;
  color: $success-color;
  margin-bottom: $spacing-lg;
}

.success-title {
  font-size: $font-size-xl;
  color: var(--text-primary);
  margin-bottom: $spacing-sm;
  font-weight: 600;
}

.success-message {
  font-size: $font-size-base;
  color: var(--text-secondary);
  margin-bottom: $spacing-xl;
  line-height: 1.6;
}

// 响应式适配
@media (max-width: $screen-sm) {
  .reset-password-container {
    padding: $spacing-lg;
    border-radius: $border-radius-base;
  }

  .page-title {
    font-size: $font-size-xl;
  }

  .input-with-btn {
    flex-direction: column;

    .verify-code-btn {
      width: 100%;
    }
  }

  .form-actions {
    flex-direction: column;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/bg/ResetPassword.vue